import React, { useEffect, useRef } from 'react';
import theme from '../config/theme';

// Modal Action interface for buttons
export interface ModalAction {
  label: string;
  onClick: () => void;
  variant?: 'primary' | 'secondary' | 'danger';
  className?: string;
  disabled?: boolean;
}

// Base Modal Props interface
export interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  message: string | React.ReactNode;
  icon?: React.ReactNode;
  actions?: ModalAction[];
  showCloseButton?: boolean;
  className?: string;
  overlayClassName?: string;
  children?: React.ReactNode;
}

// Modal Button Component
const ModalButton: React.FC<ModalAction> = ({
  label,
  onClick,
  variant = 'secondary',
  className = '',
  disabled = false,
}) => {
  let baseClasses = 'px-6 py-2 rounded-lg text-sm font-semibold transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2';

  const getVariantStyles = () => {
    if (variant === 'primary') {
      return {
        backgroundColor: theme.brandColor.red,
        color: theme.color.light100,
        border: 'none',
        hoverBg: theme.brandColor.red,
      };
    } else if (variant === 'danger') {
      return {
        backgroundColor: theme.color.error,
        color: theme.color.light100,
        border: 'none',
        hoverBg: theme.color.error,
      };
    } else {
      // secondary
      return {
        backgroundColor: theme.color.light100,
        color: theme.color.gray500,
        border: `1px solid ${theme.color.light500}`,
        hoverBg: theme.color.light100,
      };
    }
  };

  const variantStyles = getVariantStyles();
  const disabledClasses = disabled ? 'opacity-50 cursor-not-allowed' : '';

  return (
    <button
      type="button"
      onClick={onClick}
      className={`${baseClasses} ${disabledClasses} ${className}`}
      style={{
        backgroundColor: variantStyles.backgroundColor,
        color: variantStyles.color,
        border: variantStyles.border,
      }}
      onMouseEnter={(e) => {
        if (!disabled && variantStyles.hoverBg) {
          e.currentTarget.style.opacity = '0.9';
        }
      }}
      onMouseLeave={(e) => {
        if (!disabled) {
          e.currentTarget.style.opacity = disabled ? '0.5' : '1';
        }
      }}
      disabled={disabled}
    >
      {label}
    </button>
  );
};

// Main Modal Component
const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  message,
  icon,
  actions,
  showCloseButton = true,
  className = '',
  overlayClassName = '',
  children,
}) => {
  const modalRef = useRef<HTMLDivElement>(null);

  // Close modal on escape key press
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }
    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      // Save current overflow style
      const originalStyle = window.getComputedStyle(document.body).overflow;
      // Disable body scroll
      document.body.style.overflow = 'hidden';
      // Restore original overflow when modal closes
      return () => {
        document.body.style.overflow = originalStyle;
      };
    }
  }, [isOpen]);

  // If modal is not open, don't render anything
  if (!isOpen) return null;

  return (
    <div
      className={`fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4 ${overlayClassName}`}
      onClick={onClose}
    >
      <div
        ref={modalRef}
        className={`bg-white rounded-lg shadow-xl w-full mx-auto transform transition-all duration-300 ease-out scale-100 opacity-100 flex flex-col max-h-[90vh] ${className}`}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-title"
        aria-describedby="modal-description"
      >
        {/* Header - Fixed */}
        <div className="flex items-center justify-between border-b m-6 pb-4 flex-shrink-0" style={{ borderColor: theme.color.light300 }}>
          <div className="flex items-center">
            {icon && <div className="mr-4 flex-shrink-0">{icon}</div>}
            <h3 id="modal-title" className="text-xl font-semibold" style={{ color: theme.color.gray500 }}>
              {title}
            </h3>
          </div>
          {showCloseButton && (
            <button
              type="button"
              className=" hover:text-gray-600 transition-colors"
              style={{ color: theme.color.gray500 }}
              onClick={onClose}
              aria-label="Close modal"
            >
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>

        {/* Body - Scrollable */}
        <div id="modal-description" className="px-6 overflow-y-auto flex-1" style={{ color: theme.color.gray500 }}>
          {message}
        </div>

        {/* Actions (Buttons) - Fixed */}
        {actions && actions.length > 0 && (
          <div className="flex justify-end gap-3 m-6 pt-4 border-t flex-shrink-0" style={{ borderColor: theme.color.light300 }}>
            {actions.map((action, index) => (
              <ModalButton key={index} {...action} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// Confirmation Modal (Delete User Account)
export interface ConfirmationModalProps {
  isOpen: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  title: string;
  message: string | React.ReactNode;
  confirmButtonText?: string;
  cancelButtonText?: string;
  confirmButtonVariant?: 'primary' | 'danger';
  icon?: React.ReactNode;
  isLoading?: boolean;
}

export const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
  isOpen,
  onConfirm,
  onCancel,
  title,
  message,
  confirmButtonText = 'Delete',
  cancelButtonText = 'Cancel',
  confirmButtonVariant = 'danger',
  icon = (
    <div className="p-2 rounded-full bg-red-100 text-red-600">
      <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="2"
          d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
        />
      </svg>
    </div>
  ),
  isLoading = false,
}) => {
  const actions: ModalAction[] = [
    {
      label: cancelButtonText,
      onClick: onCancel,
      variant: 'secondary',
      disabled: isLoading,
    },
    {
      label: confirmButtonText,
      onClick: onConfirm,
      variant: confirmButtonVariant,
      disabled: isLoading,
    },
  ];

  return (
    <Modal
      isOpen={isOpen}
      onClose={onCancel}
      title={title}
      message={message}
      icon={icon}
      actions={actions}
      showCloseButton={!isLoading}
    />
  );
};

// Form Modal (Create New User)
export interface FormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (formData: any) => void;
  title: string;
  description?: string;
  fields: FormField[];
  submitButtonText?: string;
  cancelButtonText?: string;
  isLoading?: boolean;
  children?: React.ReactNode;
}

export interface FormField {
  name: string;
  label: string;
  type: 'text' | 'email' | 'password' | 'number' | 'tel' | 'url';
  placeholder?: string;
  required?: boolean;
  error?: string;
  value?: string;
  onChange?: (value: string) => void;
}

export const BasicModal: React.FC<FormModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  title,
  description,
  fields,
  submitButtonText = 'Create User',
  cancelButtonText = 'Cancel',
  isLoading = false,
  children,
}) => {
  const [formData, setFormData] = React.useState<Record<string, string>>({});
  const [errors, setErrors] = React.useState<Record<string, string>>({});

  // Initialize form data
  React.useEffect(() => {
    const initialData: Record<string, string> = {};
    fields.forEach(field => {
      initialData[field.name] = field.value || '';
    });
    setFormData(initialData);
  }, [fields]);

  const handleInputChange = (name: string, value: string) => {
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Validate required fields
    const newErrors: Record<string, string> = {};
    fields.forEach(field => {
      if (field.required && !formData[field.name]) {
        newErrors[field.name] = `${field.label} is required.`;
      }
    });

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    onSubmit(formData);
  };

  const actions: ModalAction[] = [
    {
      label: cancelButtonText,
      onClick: onClose,
      variant: 'secondary',
      disabled: isLoading,
    },
    {
      label: submitButtonText,
      onClick: () => handleSubmit(new Event('submit') as any),
      variant: 'primary',
      disabled: isLoading,
    },
  ];

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      message={
        <div>
          {description && (
            <p className="text-gray-600 mb-4">{description}</p>
          )}
          {children}
        </div>
      }
      actions={actions}
      showCloseButton={!isLoading}
    />
  );
};

export default Modal;
